import { inject, Injectable } from "@angular/core";
import { createEffect, ofType, Actions } from "@ngrx/effects";
import { map, catchError, of, filter, switchMap, withLatestFrom, tap, EMPTY } from "rxjs";
import { SiteActions } from "./site.actions";
import { SiteService } from "../../services/site/site.service";
import { createDefaultLoadable, ISite, IUser, onLoadableError, onLoadableLoad, onLoadableSuccess, ToastType } from "../../models";
import { selectUser } from "../user/user.selectors";
import { Store } from "@ngrx/store";
import { AppState } from "../root";
import { ISensor } from "../../models/sensor.interface";
import { selectCurrentSite, selectSites } from "./site.selectors";
import { SELECTED_SITE_KEY, SHOW_RULES_ON_PATROL_KEY, SITES_KEY } from "./site.state";
import { StatisticsService } from "../../services/site/statistics.service";
import { ToastService } from "../../services/toast/toast.service";
import { CameraService } from "../../services/camera/camera.service";
import { CameraActions } from "../camera/camera.actions";
import { selectCurrentSensor } from "../camera/camera.selectors";
import { LocalStorageService } from "../../services/local-storage/local-storage.service";
import { LicenseV2Actions } from "../../../subscriptions-plus/store/subscriptions-plus.actions";

type BulkManagePair = { action: 'link' | 'unlink'; licenseId: string; entityType: string; entityIds: string[] };

function buildLinkPairs(entityType: string, entityIds: string[], added?: string[], removed?: string[]): BulkManagePair[] {
  return [
    ...(added ?? []).map(id => ({ action: 'link' as const, licenseId: id, entityType, entityIds })),
    ...(removed ?? []).map(id => ({ action: 'unlink' as const, licenseId: id, entityType, entityIds })),
  ];
}

/**
 * Distributes licenses 1:1 to entities using round-robin assignment.
 * V2 licenses are 1:1 (each license serves one camera), so when bulk-creating
 * N cameras with M expanded license IDs, each license links to exactly one camera.
 */
function buildDistributedLinkPairs(
  entityType: string,
  entityIds: string[],
  licenseIds?: string[]
): BulkManagePair[] {
  if (!licenseIds || licenseIds.length === 0 || entityIds.length === 0) {
    return [];
  }
  return licenseIds.map((licenseId, index) => ({
    action: 'link' as const,
    licenseId,
    entityType,
    entityIds: [entityIds[index % entityIds.length]],
  }));
}

@Injectable()
export class SiteEffects {
  private actions$ = inject(Actions);
  private siteService = inject(SiteService);
  private cameraService = inject(CameraService);
  private statisticsService = inject(StatisticsService);
  private store$ = inject(Store<AppState>);
  private toastService = inject(ToastService);
  private localStorage = inject(LocalStorageService);

  loadSites$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.loadSites),
    withLatestFrom(this.store$.select(selectUser)),
    filter(([_, user]) => !!user),
    switchMap(([_, user]) => {
      const currentUser = user as IUser;
      return this.siteService.getSites(currentUser.organisation_id).pipe(
        map(sites => {
          const sortedSites = [...sites].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'case' })
          );
          return SiteActions.loadSitesSuccess({ sites: sortedSites });
        }),
        catchError(error => of(SiteActions.loadSitesFailure({ error })))
      );
    })
  )
  );

  loadCameras$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.loadSensors),
    switchMap((action) => {
      return this.siteService.getCameras(action.site.id).pipe(
        map(sensors => {
          const cameras = (sensors as any)["cameras"] as ISensor[];
          const sortedSensors = [...cameras].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'case' })
          );
          return SiteActions.loadSensorsSuccess({ site: action.site, sensors: sortedSensors });
        }),
        catchError(error => of(SiteActions.loadSensorsFailure({ site: action.site, error })))
      );
    })
  )
  );

  registerSite$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.registerSite),
    withLatestFrom(this.store$.select(selectUser)),
    filter(([_, user]) => !!user),
    switchMap(([action, user]) => {
      const currentUser = user as IUser;
      return this.siteService.registerSite(currentUser, action.site).pipe(
        map(site => {
          this.toastService.showToast("Registered site successfully", ToastType.SUCCESS)
          return SiteActions.registerSiteSuccess({ site })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to create site", ToastType.ERROR)
          return of(SiteActions.registerSiteFailure({ error }))
        })
      );
    })
  ));

  createSensors$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.createSensors),
    switchMap((action) => {
      return this.cameraService.createCameras(action.sensors).pipe(
        switchMap(sensors => {
          this.toastService.showToast("Created cameras successfully", ToastType.SUCCESS);
          this.store$.dispatch(SiteActions.loadCurrentSiteSensors());

          // Fire-and-forget: distribute V2 licenses 1:1 to newly created cameras
          const pairs = buildDistributedLinkPairs('camera', sensors.map((s: ISensor) => s.id), action.additionalLicenseIds);
          if (pairs.length > 0) {
            this.store$.dispatch(LicenseV2Actions.bulkManageLinks({ pairs }));
          }

          return of(SiteActions.createSensorsSuccess({ sensors: sensors }));
        }),
        catchError(error => {
          this.toastService.showToast("Failed to create Cameras", ToastType.ERROR)
          return of(SiteActions.createSensorsError({ siteIDs: action.sensors.map(s => s.site_id), error }))
        })
      );
    })
  ));

  updateSensor$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.updateSensor),
    switchMap((action) => {
      return this.cameraService.updateCamera(action.sensor).pipe(
        switchMap(sensor => {
          this.toastService.showToast("Updated camera successfully", ToastType.SUCCESS);

          // Fire-and-forget: sync V2 license link/unlink changes for updated camera
          const pairs = buildLinkPairs('camera', [sensor.id], action.additionalLicenseIds, action.removedLicenseIds);
          if (pairs.length > 0) {
            this.store$.dispatch(LicenseV2Actions.bulkManageLinks({ pairs }));
          }

          return of(SiteActions.updateSensorSuccess({ sensor: sensor }));
        }),
        catchError(error => {
          this.toastService.showToast("Failed to update Camera", ToastType.ERROR)
          return of(SiteActions.updateSensorError({ sensor: action.sensor, error }))
        })
      );
    })
  ));

  updateSensorSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.updateSensorSuccess),
    switchMap((action) => {
     return of(CameraActions.setSelectedSensor({sensor: onLoadableSuccess({...createDefaultLoadable(), ...action.sensor})}))
    })
  ));

  enableSensors$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.enableSensors),
    switchMap((action) => {
      return this.cameraService.enableCameras(action.sensors.map(sensor => sensor.id)).pipe(
        map(_ => {
          this.toastService.showToast("Enabled cameras successfully", ToastType.SUCCESS)
          return SiteActions.enableSensorsSuccess({ sensors: action.sensors })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to enable cameras", ToastType.ERROR)
          return of(SiteActions.enableSensorsFailure({ sensors: action.sensors, error }))
        })
      );
    })
  ));

  disableSensors$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.disableSensors),
    switchMap((action) => {
      return this.cameraService.disableCameras(action.sensors.map(sensor => sensor.id)).pipe(
        map(_ => {
          this.toastService.showToast("Disabled cameras successfully", ToastType.SUCCESS)
          return SiteActions.disableSensorsSuccess({ sensors: action.sensors })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to disable cameras", ToastType.ERROR)
          return of(SiteActions.disableSensorsFailure({ sensors: action.sensors, error }))
        })
      );
    })
  ));

  enableSensorsSensorState$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.enableSensors),
    withLatestFrom(this.store$.select(selectCurrentSensor)),
    switchMap(([action, currentSensor]) => {
      if (currentSensor == null) {
        return EMPTY;
      }
      const sensors: ISensor[] = action.sensors;
      const currentSensorInSensors: boolean = sensors.some(sensor => sensor.id === currentSensor.id);
      if (currentSensorInSensors) {
        return of(CameraActions.setSelectedSensor({sensor: onLoadableLoad(currentSensor)}));
      }
      return EMPTY;
    })
  ));

  enableSensorsSuccessSensorState$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.enableSensorsSuccess),
    withLatestFrom(this.store$.select(selectCurrentSensor)),
    switchMap(([action, currentSensor]) => {
      if (currentSensor == null) {
        return EMPTY;
      }
      const sensors: ISensor[] = action.sensors;
      const currentSensorInSensors: boolean = sensors.some(sensor => sensor.id === currentSensor.id);
      if (currentSensorInSensors) {
        return of(CameraActions.setSelectedSensor({sensor: onLoadableSuccess({...currentSensor, enabled: true})}));
      }
      return EMPTY;
    })
  ));

  enableSensorsFailureState$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.enableSensorsFailure),
    withLatestFrom(this.store$.select(selectCurrentSensor)),
    switchMap(([action, currentSensor]) => {
      if (currentSensor == null) {
        return EMPTY;
      }
      const sensors: ISensor[] = action.sensors;
      const currentSensorInSensors: boolean = sensors.some(sensor => sensor.id === currentSensor.id);
      if (currentSensorInSensors) {
        return of(CameraActions.setSelectedSensor({sensor: onLoadableError(currentSensor, action.error)}));
      }
      return EMPTY;
    })
  ));

  disableSensorsSensorState$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.disableSensors),
    withLatestFrom(this.store$.select(selectCurrentSensor)),
    switchMap(([action, currentSensor]) => {
      if (currentSensor == null) {
        return EMPTY;
      }
      const sensors: ISensor[] = action.sensors;
      const currentSensorInSensors: boolean = sensors.some(sensor => sensor.id === currentSensor.id);
      if (currentSensorInSensors) {
        return of(CameraActions.setSelectedSensor({sensor: onLoadableLoad(currentSensor)}));
      }
      return EMPTY;
    })
  ));

  disableSensorsSuccessSensorState$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.disableSensorsSuccess),
    withLatestFrom(this.store$.select(selectCurrentSensor)),
    switchMap(([action, currentSensor]) => {
      if (currentSensor == null) {
        return EMPTY;
      }
      const sensors: ISensor[] = action.sensors;
      const currentSensorInSensors: boolean = sensors.some(sensor => sensor.id === currentSensor.id);
      if (currentSensorInSensors) {
        return of(CameraActions.setSelectedSensor({sensor: onLoadableSuccess({...currentSensor, enabled: false})}));
      }
      return EMPTY;
    })
  ));

  disableSensorsFailureState$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.disableSensorsFailure),
    withLatestFrom(this.store$.select(selectCurrentSensor)),
    switchMap(([action, currentSensor]) => {
      if (currentSensor == null) {
        return EMPTY;
      }
      const sensors: ISensor[] = action.sensors;
      const currentSensorInSensors: boolean = sensors.some(sensor => sensor.id === currentSensor.id);
      if (currentSensorInSensors) {
        return of(CameraActions.setSelectedSensor({sensor: onLoadableError(currentSensor, action.error)}));
      }
      return EMPTY;
    })
  ));

  deleteSensor$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.deleteSensor),
    switchMap((action) => {
      return this.cameraService.deleteCamera(action.sensor).pipe(
        map(sensor => {
          this.toastService.showToast("Deleted camera successfully", ToastType.SUCCESS)
          return SiteActions.deleteSensorSuccess({ sensor: action.sensor })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to delete Camera", ToastType.ERROR)
          return of(SiteActions.deleteSensorError({ sensor: action.sensor, error }))
        })
      );
    })
  ));

  updateSite$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.updateSite),
    switchMap((action) => {
      return this.siteService.updateSite(action.site).pipe(
        map(site => {
          this.toastService.showToast("Updated site successfully", ToastType.SUCCESS)
          return SiteActions.updateSiteSuccess({ site: action.site })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to update site", ToastType.ERROR)
          return of(SiteActions.updateSiteFailure({ site: action.site, error }))
        })
      );
    }))
  );

  deleteSite$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.deleteSite),
    switchMap((action) => {
      return this.siteService.deleteSite(action.site).pipe(
        map(site => {
          this.toastService.showToast("Deleted site successfully", ToastType.SUCCESS)
          return SiteActions.deleteSiteSuccess({ site: action.site })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to delete site", ToastType.ERROR)
          return of(SiteActions.deleteSiteFailure({ site: action.site, error }))
        })
      );
    }))
  );

  loadCurrentSiteCameras$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.loadCurrentSiteSensors),
    withLatestFrom(this.store$.select(selectCurrentSite)),
    filter(([_, site]) => !!site),
    switchMap(([_, site]) => {
      const currentSite = site as ISite;
      return of(SiteActions.loadSensors({ site: currentSite }))
    })
  )
  );

  loadSensorStats$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.loadSensorStatistics),
    withLatestFrom(this.store$.select(selectCurrentSite)),
    filter(([_, site]) => !!site),
    switchMap(([action, site]) => {
      const ids = action.sensors.map(e => e.id)
      const currentSite = site as ISite;

      return this.statisticsService.getCameraStatistics(ids, currentSite.timezone).pipe(
        map(statistics => SiteActions.loadSensorStatisticsSuccess({ sensors: action.sensors, statistics: (statistics as any[]).map(e => e["stats"]) })),
        catchError(error => of(SiteActions.loadSensorStatisticsFailure({ error })))
      );
    })
  ));

  loadSiteStats$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.loadSiteStatistics),
    withLatestFrom(this.store$.select(selectCurrentSite)),
    filter(([_, site]) => !!site),
    switchMap(([_, site]) => {
      const currentSite = site as ISite;
      return this.statisticsService.getSiteStatistics([currentSite.id], currentSite.timezone).pipe(
        map(statistics => SiteActions.loadSiteStatisticsSuccess({ site: currentSite, statistics: (statistics as any[]).map(e => e["stats"]) })),
        catchError(error => of(SiteActions.loadSiteStatisticsFailure({ site: currentSite, error })))
      );
    })
  ));


  // region Storage
  // Logic for storing information so that it is available on reloads

  storeCurrentSite$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.setCurrentSite),
    tap((action) => {
      sessionStorage.setItem(SELECTED_SITE_KEY, action.site.id)
    })
  ), { dispatch: false }
  );

  clearCurrentSite$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.clearCurrentSite),
    tap((action) => {
      sessionStorage.removeItem(SELECTED_SITE_KEY);
    })
  ), { dispatch: false }
  );

  storeSites$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.loadSitesSuccess, SiteActions.updateSiteSuccess, SiteActions.registerSiteSuccess),
    withLatestFrom(this.store$.select(selectSites)),
    tap(([_, sites]) => {
      sessionStorage.setItem(SITES_KEY, JSON.stringify(sites))
    })
  ), { dispatch: false }
  );

  storeShowRulesOnPatrol$ = createEffect(() => this.actions$.pipe(
    ofType(SiteActions.setShowRulesOnPatrol),
    tap((action) => {
      this.localStorage.setItem(SHOW_RULES_ON_PATROL_KEY, JSON.stringify(action.enabled));
    })
  ), { dispatch: false }
  );

  //endregion

}
